import { createTheme, type Theme } from '@mui/material/styles';
import { DARK, FONT_BODY, FONT_DISPLAY, LIGHT, RADIUS, type Tokens } from './tokens';

/**
 * The MUI theme, built FROM the app's existing tokens rather than the other way round.
 *
 * MUI is here for generic chrome only — buttons, dialogs, tables, selects, snackbars. The
 * map, the doc pills and the doc-nav band keep their own CSS, because no MUI primitive
 * matches them and forcing one would change the look, which is out of scope.
 *
 * Anything that overrides a MUI default here exists to make a MUI component indistinguishable
 * from the hand-rolled control it replaces. If an override is not doing that, delete it.
 */
function build(t: Tokens, mode: 'light' | 'dark'): Theme {
  return createTheme({
    palette: {
      mode,
      background: { default: t.bg, paper: t.panel },
      text: { primary: t.ink, secondary: t.inkDim, disabled: t.inkFaint },
      primary: { main: t.accent, contrastText: '#fff' },
      divider: t.line,
    },
    shape: { borderRadius: RADIUS },
    typography: {
      fontFamily: FONT_BODY,
      // .brand uses the display face; nothing else in the app does.
      h1: { fontFamily: FONT_DISPLAY },
      h2: { fontFamily: FONT_DISPLAY },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          // .btn today: 1px border, panel background, dim ink, 10px radius.
          root: { borderRadius: 10, fontSize: 14 },
          outlined: { borderColor: t.line, color: t.inkDim, backgroundColor: t.panel },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundColor: t.panel, backgroundImage: 'none', border: `1px solid ${t.line}` },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { backgroundColor: t.panel2, borderRadius: 10 },
          notchedOutline: { borderColor: t.line },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: t.panel2, color: t.ink, border: `1px solid ${t.line}`, fontSize: 12.5 },
        },
      },
      // The top bar's "…" overflow menus (plan 1.3) — the first MUI components rendered by
      // the app. Styled to be indistinguishable from `.filterpanel`, the hand-rolled dropdown
      // they sit beside: panel background, 1px line border, radius 10, the same shadow, and
      // rows shaped like `.filterrow`.
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: t.panel,
            backgroundImage: 'none',
            color: t.ink,
            border: `1px solid ${t.line}`,
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,.28)',
          },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: 13,
            minHeight: 0,
            padding: '7px 9px',
            gap: 9,
            color: t.ink,
            '&:hover': { backgroundColor: t.panel2 },
            '&.Mui-selected': { backgroundColor: t.panel2 },
            '&.Mui-selected:hover': { backgroundColor: t.panel2 },
          },
        },
      },
      // The bell's panel (plan Phase 3) — the same `.filterpanel` look the "…" menus carry.
      // Menu's paper override above does not reach a bare Popover, so the values are stated
      // again here rather than shared: two components, one look, each named where MUI reads it.
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: t.panel,
            backgroundImage: 'none',
            color: t.ink,
            border: `1px solid ${t.line}`,
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,.28)',
          },
        },
      },
      // The bell's unread count (plan Phase 3): the red badge. Sized to sit inside a 34px
      // top-bar button without growing it.
      MuiBadge: {
        styleOverrides: {
          badge: {
            backgroundColor: '#e5484d',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            height: 15,
            minWidth: 15,
            padding: '0 4px',
          },
        },
      },
    },
  });
}

export const darkTheme = build(DARK, 'dark');
export const lightTheme = build(LIGHT, 'light');

export const themeFor = (isLight: boolean): Theme => (isLight ? lightTheme : darkTheme);
